import React, { useState, useEffect, useContext } from 'react';
import { Edit, Trash2, Plus, Eye, Search, X, ExternalLink, Filter, SortAsc } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from './ui/select';
import { useNavigate, useLocation } from 'react-router-dom';

import { ProductModal } from './ProductModal';
import { toast } from 'sonner';
import UserContext from '../context/UserContext';
import ProductService from '../services/productService';
import categoryService from '../services/categoryService.js';

// Import component-specific CSS (SOLID Principle)
import './ProductsTable.css';

interface Category {
  id: number;
  name: string;
  name_ar?: string;
}

interface Product {
  id: number;
  product_name: string;
  product_name_ar?: string;
  active_ingredient?: string;
  registration_number?: string;
  crop?: string;
  season_year?: string;
  usage_rate?: string;
  pest?: string;
  producing_company?: string;
  country?: string;
  exporting_company?: string;
  experimentation_26_25?: string;
  notes?: string;
  branch?: string;
  season?: string;
  category?: Category;
  createdAt?: string;
  updatedAt?: string;
}

export function ProductsTable() {
  const navigate = useNavigate();
  const location = useLocation();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('name-asc');
  const [allCategories, setAllCategories] = useState<Category[]>([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalProducts: 0
  });

  // Permissions from context (clean single definition)
  const { permissions } = useContext(UserContext);
  const permissionArray = Array.isArray(permissions) ? (permissions as string[]) : [];
  const has = (perm: string): boolean => permissionArray.includes(perm);

  const canManageProducts = has('manage_products');
  const canViewProducts = has('view_products') || canManageProducts;
  const canCreateProducts = canManageProducts || has('create_product');
  const canEditProducts = canManageProducts || has('edit_any_product');
  const canDeleteProducts = canManageProducts || has('delete_product');

  const hasAnyActions = canEditProducts || canDeleteProducts || canManageProducts;

  // Load categories on component mount
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const response = await categoryService.getCategories();
        const validCategories = Array.isArray(response.data) ? response.data :
                               Array.isArray(response) ? response : [];
        const filteredCategories = validCategories.filter((category: Category) =>
          category && category.id != null && category.name && category.name.trim().length > 0
        );
        setAllCategories(filteredCategories);
      } catch (error) {
        console.error('Failed to load categories:', error);
        setAllCategories([]);
      }
    };

    loadCategories();
  }, []);

  // Set selectedCategory from navigation state
  useEffect(() => {
    if (location.state?.selectedCategory) {
      setSelectedCategory(location.state.selectedCategory);
    }
  }, [location]);

  // Load products on component mount and listen for changes
  useEffect(() => {
    if (canViewProducts) {
      // Don't call loadProducts here - let the pagination effect handle it
      // This prevents duplicate API calls on initial mount
      console.log('Products mounted - pagination effect will handle initial load');
    } else {
      console.log('Cannot view products - permission denied');
      setLoading(false);
    }

    // Listen for product changes
    const handleProductsChanged = (event: any) => {
      console.log('Products changed event received:', event.detail);
      // Immediately refresh products when changes are detected
      loadProducts(true); // Bypass cache for instant update
    };

    window.addEventListener('productsChanged', handleProductsChanged);

    return () => {
      window.removeEventListener('productsChanged', handleProductsChanged);
    };
  }, [canViewProducts]);

  // Trigger product reload when search, category, sort, or page changes
  useEffect(() => {
    if (!canViewProducts) return;

    // Use latest search term inside the timeout explicitly
    const currentSearchTerm = searchTerm;
    const currentCategory = selectedCategory;
    const currentSort = sortBy;
    const currentPage = pagination.currentPage;

    const timer = setTimeout(() => {
      // Create bypass cache parameter, but pass the parameters explicitly to avoid stale closures
      loadProducts(false, currentSearchTerm, currentCategory, currentSort, currentPage);
    }, 300); // 300ms debounce

    return () => clearTimeout(timer);
  }, [searchTerm, selectedCategory, sortBy, pagination.currentPage, canViewProducts]);

  const loadProducts = async (
    bypassCache = false,
    explicitSearchTerm = searchTerm,
    explicitCategory = selectedCategory,
    explicitSort = sortBy,
    explicitPage = pagination.currentPage
  ) => {
    try {
      console.log('Loading products...', bypassCache ? '(bypassing cache)' : '');
      setLoading(true);
      setError(null);
      
      if (bypassCache) {
        ProductService.clearCache();
      }

      // Map sortBy to API format
      const [sortField, sortDir] = explicitSort.split('-');
      let apiSortBy = sortField === 'name' ? 'product_name' : 
                      (sortField === 'created' ? 'createdAt' : 
                      (sortField === 'registration' ? 'registration_number' : 'createdAt'));
      let apiSortOrder = sortDir.toUpperCase();

      const params: any = {
        page: explicitPage,
        limit: 10,
        sortBy: apiSortBy,
        sortOrder: apiSortOrder
      };

      if (explicitSearchTerm) {
        params.search = explicitSearchTerm;
      }

      if (explicitCategory && explicitCategory !== 'all') {
        params.category_id = explicitCategory;
      }
      
      const response = await ProductService.getAllProducts(params);
      console.log('Products response:', response);
      const productsData = response.products || [];
      setProducts(productsData);
      
      // Extract pagination metadata
      if (response.pagination) {
        setPagination(prev => ({
          ...prev,
          totalPages: response.pagination.totalPages,
          totalProducts: response.pagination.total
        }));
      }
      
      console.log('Products loaded:', productsData?.length || 0);
    } catch (err: any) {
      console.error('Error loading products:', err);
      setError(err.message || 'Failed to load products');

      // If user lacks permissions, they can still view the empty table
      if (err.message?.includes('permission')) {
        setError('Access Denied: You do not have permission to view products');
      } else {
        toast.error('Failed to load products');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    // Navigate to add product page
    navigate('/product/add');
  };

  const handleEdit = (product: Product) => {
    // Navigate to product detail page in edit mode
    navigate(`/product/${product.id}`, {
      state: { editMode: true }
    });
  };

  const handleView = (product: Product) => {
    // Navigate to product detail page in view mode
    navigate(`/product/${product.id}`, {
      state: { editMode: false }
    });
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Are you sure you want to delete this product?')) return;

    try {
      await ProductService.deleteProduct(id);
      setProducts(products.filter(product => product.id !== id));
      toast.success('Product deleted successfully');
    } catch (error: any) {
      console.error('Error deleting product:', error);
      toast.error(error.response?.data?.error?.message || 'Failed to delete product');
    }
  };

  const handleSave = async (productData: Omit<Product, 'id'>) => {
    try {
      if (editingProduct) {
        // Update existing product
        const updatedProduct = await ProductService.updateProduct(editingProduct.id, productData);
        setProducts(products.map(product =>
          product.id === editingProduct.id ? updatedProduct.product : product
        ));
        toast.success('Product updated successfully');
      } else {
        // Create new product
        const newProduct = await ProductService.createProduct(productData);
        setProducts([...products, newProduct.product]);

        // Auto-refresh after creation to ensure all data is loaded
        loadProducts(true);

        toast.success('Product created successfully');
      }
      setIsModalOpen(false);
    } catch (error: any) {
      console.error('Error saving product:', error);
      toast.error(error.response?.data?.error?.message || 'Failed to save product');
    }
  };

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Products Management</CardTitle>
          {canCreateProducts && (
            <Button
              onClick={handleAdd}
              className="bg-[#3B82F6] hover:bg-[#2563EB]"
              style={{ background: 'linear-gradient(90deg, #3fa46eff, #246844ff)'}}
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Product
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {/* Filter and Search Controls */}
          <div className="mb-6 space-y-4">
            <div className="flex items-center gap-4">
              {/* Search Input */}
              <div className="flex-1">
                <div className="flex items-center gap-2 px-3 py-2 bg-muted/60 border border-border rounded-full shadow-sm focus-within:ring-2 focus-within:ring-primary/40 focus-within:border-primary/60 transition-all">
                  <Search className="w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search products..."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setPagination(prev => ({ ...prev, currentPage: 1 }));
                    }}
                    className="border-0 bg-transparent shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 px-0 text-sm text-left"
                  />
                </div>
              </div>

              {/* Category Filter */}
              <div className="flex items-center gap-2 w-48">
                <Filter className="w-4 h-4 text-muted-foreground" />
                <Select
                  value={selectedCategory}
                  onValueChange={(val) => {
                    setSelectedCategory(val);
                    setPagination(prev => ({ ...prev, currentPage: 1 }));
                  }}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Filter by category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {allCategories.map((category) => (
                      <SelectItem key={category.id} value={category.id.toString()}>
                        {category.name}
                        {category.name_ar && ` (${category.name_ar})`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Sort Options */}
              <div className="flex items-center gap-2 w-48">
                <SortAsc className="w-4 h-4 text-muted-foreground" />
                <Select
                  value={sortBy}
                  onValueChange={(val) => {
                    setSortBy(val);
                    setPagination(prev => ({ ...prev, currentPage: 1 }));
                  }}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Sort by" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="name-asc">Name (A-Z)</SelectItem>
                    <SelectItem value="name-desc">Name (Z-A)</SelectItem>
                    <SelectItem value="created-desc">Newest First</SelectItem>
                    <SelectItem value="created-asc">Oldest First</SelectItem>
                    <SelectItem value="registration-asc">Registration (A-Z)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Clear Filters Button */}
              {(searchTerm || selectedCategory !== 'all' || sortBy !== 'name-asc') && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setSearchTerm('');
                    setSelectedCategory('all');
                    setSortBy('name-asc');
                    setPagination(prev => ({ ...prev, currentPage: 1 }));
                  }}
                >
                  <X className="w-3 h-3 mr-1" />
                  Clear Filters
                </Button>
              )}
            </div>

            {/* Active Filters Summary */}
            {(searchTerm || selectedCategory !== 'all' || sortBy !== 'name-asc') && (
              <div className="flex flex-wrap gap-2 text-sm text-muted-foreground">
                <span>Filters:</span>
                {searchTerm && <span className="bg-muted px-2 py-1 rounded">Search: "{searchTerm}"</span>}
                {selectedCategory !== 'all' && (
                  <span className="bg-muted px-2 py-1 rounded">
                    Category: {allCategories.find(c => c.id.toString() === selectedCategory)?.name || 'Unknown'}
                  </span>
                )}
                {sortBy !== 'name-asc' && (
                  <span className="bg-muted px-2 py-1 rounded">
                    Sort: {sortBy === 'name-desc' ? 'Name (Z-A)' :
                           sortBy === 'created-desc' ? 'Newest First' :
                           sortBy === 'created-asc' ? 'Oldest First' :
                           sortBy === 'registration-asc' ? 'Registration (A-Z)' : 'Unknown'}
                  </span>
                )}
              </div>
            )}

            {/* Results Summary */}
            {loading ? (
              <div className="text-sm text-muted-foreground">Loading products...</div>
            ) : (
              <div className="text-sm text-muted-foreground">
                Showing {pagination.totalProducts > 0 ? ((pagination.currentPage - 1) * 10) + 1 : 0} to {Math.min(pagination.currentPage * 10, pagination.totalProducts)} of {pagination.totalProducts} products
              </div>
            )}
          </div>

          {loading ? (
            <div className="text-center py-8">Loading products...</div>
          ) : error ? (
            <div className="text-center py-8 text-destructive">{error}</div>
          ) : (
            <div className="rounded-lg border border-border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>PD Number</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Active Ingredient</TableHead>
                    <TableHead>Crop</TableHead>
                    <TableHead>Branch</TableHead>
                    {hasAnyActions && <TableHead className="text-right">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {products.map((product) => (
                    <TableRow key={product.id}>
                      <TableCell className="text-left">
                        <div>
                          <div className="font-medium">{product.product_name}</div>
                          {product.product_name_ar && (
                            <div className="text-sm text-muted-foreground">{product.product_name_ar}</div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-left">
                        <div className="text-sm">{product.registration_number || '-'}</div>
                      </TableCell>
                      <TableCell className="text-left">
                        {product.category ? (
                          <div>
                            <div className="text-sm font-medium">{product.category.name}</div>
                            {product.category.name_ar && (
                              <div className="text-sm text-muted-foreground">{product.category.name_ar}</div>
                            )}
                          </div>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-left">
                        <div className="truncate max-w-xs text-sm">{product.active_ingredient || '-'}</div>
                      </TableCell>
                      <TableCell className="text-left">
                        <div className="text-sm">{product.crop || '-'}</div>
                      </TableCell>
                      <TableCell className="text-left">
                        <div className="text-sm">{product.branch || '-'}</div>
                      </TableCell>
                      {hasAnyActions && (
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            {/* View product details */}
                            {canViewProducts && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleView(product)}
                                title="View Product Details"
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                            )}
                            {/* Edit product */}
                            {canEditProducts && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleEdit(product)}
                                title="Edit Product"
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                            )}
                            {/* Delete product */}
                            {canDeleteProducts && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDelete(product.id)}
                                className="text-destructive hover:text-destructive"
                                title="Delete Product"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {!loading && products.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  No products found
                </div>
              )}
            </div>
          )}

          {/* Pagination Controls */}
          {!loading && pagination.totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <div className="text-sm text-muted-foreground">
                Showing {((pagination.currentPage - 1) * 10) + 1} to {Math.min(pagination.currentPage * 10, pagination.totalProducts)} of {pagination.totalProducts} products
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage - 1 }))}
                  disabled={pagination.currentPage === 1}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage + 1 }))}
                  disabled={pagination.currentPage === pagination.totalPages}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <ProductModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSave}
        product={editingProduct}
      />
    </>
  );
}
